import React from 'react';

import { useGetDepartment, useGetList } from '@vlabs/api/artemy/client';
import type { ListId } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { useHasPermission } from '@vlabs/permissions';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { useTranslation } from 'react-i18next';

import st from './ListGeneralInfo.module.sass';
import { EditListButton } from '../../list-list-page/buttons/EditListButton';

export interface ListGeneralInfoProps {
  listId: ListId;
}

const displayName = 'ListGeneralInfo';
const testid = buildTestid(displayName);

export function ListGeneralInfo({ listId }: ListGeneralInfoProps): React.JSX.Element {
  const { t } = useTranslation();
  const getList = useGetList(listId);
  const canEditList = useHasPermission('list', 'update');

  const listName = getList.data?.user_data;
  const ownerDepartmentId = getList.data?.owner_department;
  const getOwnerDepartment = useGetDepartment(ownerDepartmentId ?? '', {
    query: { enabled: !!ownerDepartmentId },
  });
  const ownerDepartmentName = getOwnerDepartment.data?.name ?? ownerDepartmentId ?? '-';
  const parsedCreateTime = getList.data?.create_time ? parseISOToLocale(getList.data?.create_time) : '-';
  const parsedLastUpdateTime = getList.data?.last_update_time ? parseISOToLocale(getList.data?.last_update_time) : '-';

  return (
    <div
      className={st.Info}
      {...testid()}
    >
      <header className={st.InfoHeader}>
        <h6>{t('list-details-page:info block.title')}</h6>
        {!!canEditList && (
          <EditListButton
            kind="secondary"
            listId={listId}
            size="small"
            variant="filled"
          />
        )}
      </header>
      <div className={st.InfoBody}>
        <div className={st.Row}>
          <span
            className={st.Label}
            {...testid('ListName', 'Label')}
          >
            {t('list-details-page:info block.name')}
          </span>
          <span
            className={`${st.Value} ${st.Value_accent}`}
            {...testid('ListName', 'Value')}
          >
            <TruncatedText
              hasTruncation
              tooltipContent={getList.data?.user_data ?? ''}
            >
              {listName ?? '-'}
            </TruncatedText>
          </span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span
            className={st.Label}
            {...testid('OwnerDepartment', 'Label')}
          >
            {t('list-details-page:info block.owner department')}
          </span>
          <span
            className={st.Value}
            {...testid('OwnerDepartment', 'Value')}
          >
            {ownerDepartmentName}
          </span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span
            className={st.Label}
            {...testid('Created', 'Label')}
          >
            {t('list-details-page:info block.created')}
          </span>
          <span
            className={st.Value}
            {...testid('Created', 'Value')}
          >
            {parsedCreateTime}
          </span>
        </div>

        <div className={st.Divider} />

        <div className={st.Row}>
          <span
            className={st.Label}
            {...testid('LastChanges', 'Label')}
          >
            {t('list-details-page:info block.last changes')}
          </span>
          <span
            className={st.Value}
            {...testid('LastChanges', 'Value')}
          >
            {parsedLastUpdateTime}
          </span>
        </div>
        <div className={st.Divider} />
      </div>
    </div>
  );
}
